import { useEffect, useRef } from "react";

import { useActiveTask, useAgentSdk, useAgentStore, useLogger } from "@/sdk";

import { useDismissalStatus } from "../data/useMorningPaperDismissal";

// The flag is passed in (not read via useFeatures) so Home.tsx can resolve
// it from LaunchDarkly inside the existing pre-workspace loading gate. Without
// this, useFeatures would synchronously return the false default and the
// workspace would render briefly before swapping to the morning paper once LD
// responded — a visible first-paint flash.
export function useShouldShowMorningPaper(isMorningPaperEnabled: boolean): boolean {
    const activeTask = useActiveTask();
    const logger = useLogger({ module: "MorningPaperVisibility" });
    const hasAlreadyLoggedSkipReason = useRef(false);
    const hasEvaluatedPrePark = useRef(false);
    const didPrePark = useRef(false);
    const sdk = useAgentSdk();
    const activity = useAgentStore((state) => state.activity);

    const { data: dismissalStatus, isLoading: isDismissalStatusLoading } = useDismissalStatus(isMorningPaperEnabled);
    const dismissedToday = dismissalStatus?.dismissed ?? false;
    const isOffline = activity === sdk.offlineAgentActivity;
    const shouldShow = isMorningPaperEnabled && !activeTask && !dismissedToday && !isDismissalStatusLoading;

    useEffect(() => {
        if (hasAlreadyLoggedSkipReason.current) return;
        if (!isMorningPaperEnabled || !dismissedToday) return;
        hasAlreadyLoggedSkipReason.current = true;
        logger.info(
            { dismissedAt: dismissalStatus?.dismissedAt },
            "Morning paper: skipping render — already dismissed for today",
        );
    }, [dismissedToday, isMorningPaperEnabled, dismissalStatus?.dismissedAt, logger]);

    // Park the expert in Offline before showing the morning paper.
    // The morning paper only renders once the expert is confirmed Offline,
    // so the ritual inside the component won't need to change activity.
    // For partners where Offline triggers a logout, the expert gets logged
    // out and returns already in Offline — the morning paper then shows safely.
    useEffect(() => {
        if (hasEvaluatedPrePark.current) return;
        if (!shouldShow) return;
        if (!activity) return;

        hasEvaluatedPrePark.current = true;

        if (isOffline) return; // Already Offline — no parking needed

        didPrePark.current = true;
        logger.info({ activity }, "Morning paper: parking expert in Offline before showing");
        void sdk.setAgentActivity(sdk.offlineAgentActivity);
    }, [shouldShow, activity, isOffline, sdk, logger]);

    if (!shouldShow) {
        if (activeTask && !hasAlreadyLoggedSkipReason.current) {
            hasAlreadyLoggedSkipReason.current = true;
            logger.info({ taskId: activeTask.id }, "Morning paper: skipping — active task present");
        }
        return false;
    }
    // If we pre-parked the expert, never show the paper in this component lifecycle.
    // The expert will logout and return in a fresh mount already in Offline.
    if (didPrePark.current) {
        logger.info("Morning paper: skipping — pre-park in progress, waiting for logout/reload");
        return false;
    }
    // Final check: only show if shouldShow is true AND agent is offline
    return shouldShow && isOffline;
}
